import AppVerticalMenu from "../components/AppVerticalNav";
import { StyledSidebar, StyledSidebarMenu } from "./index.styled";
import { IRouteConfig } from "@lib/types/Routes";
import { useTypedDispatch, useTypedSelector } from "@lib/redux";
import AdminSidebarFooter from "./AdminSidebarFooter";
import { IoIosArrowBack } from "react-icons/io";
import { updateSidebar } from "@lib/redux/slices/screen";
import { StyledSidebarCollapseBtn } from "@lib/components/StyledComponents/layout";
import { useEffect, useRef } from "react";

export type AdminSidebarMenuProps = {
  collapsed?: boolean;
};

type Props = {
  routesConfig: IRouteConfig[];
  menuProps?: AdminSidebarMenuProps;
};

const AdminSidebar = ({ routesConfig, menuProps }: Props) => {
  const dispatch = useTypedDispatch();
  const { sidebar, window: windowState } = useTypedSelector(
    (state) => state.screen,
  );

  const isMobile = windowState?.width < windowState?.breakpointHide;

  // Store the original collapsed state to restore it when component unmounts
  const originalCollapsedRef = useRef<boolean | null>(null);

  // Apply menuProps.collapsed temporarily - only for this page
  useEffect(() => {
    if (menuProps?.collapsed !== undefined) {
      // Save the original state if not already saved
      if (originalCollapsedRef.current === null) {
        originalCollapsedRef.current = sidebar.collapsed;
      }
      // Apply the temporary collapsed state
      dispatch(updateSidebar({ collapsed: menuProps.collapsed }));
    }

    // Cleanup: restore original state when component unmounts or menuProps changes
    return () => {
      if (
        menuProps?.collapsed !== undefined &&
        originalCollapsedRef.current !== null
      ) {
        dispatch(updateSidebar({ collapsed: originalCollapsedRef.current }));
        originalCollapsedRef.current = null;
      }
    };
  }, [menuProps?.collapsed, dispatch]);

  const handleCollapseSidebar = () => {
    if (windowState?.width < windowState?.breakpointHide) {
      dispatch(updateSidebar({ hidden: !sidebar.hidden }));
    } else {
      dispatch(updateSidebar({ collapsed: !sidebar.collapsed }));
    }
  };

  // Use menuProps.collapsed if provided, otherwise use Redux state
  const sidebarCollapsed =
    menuProps?.collapsed !== undefined
      ? menuProps.collapsed
      : sidebar.collapsed;

  return (
    <StyledSidebar collapsed={sidebarCollapsed}>
      <StyledSidebarMenu width={sidebar.width}>
        {!sidebar.hidden && !isMobile && (
          <StyledSidebarCollapseBtn
            className="main-sidebar-collapse-btn"
            icon={<IoIosArrowBack />}
            collapsed={sidebarCollapsed}
            onClick={handleCollapseSidebar}
            defaultHide
          />
        )}

        <AppVerticalMenu routesConfig={routesConfig} />

        <AdminSidebarFooter />
      </StyledSidebarMenu>
    </StyledSidebar>
  );
};

export default AdminSidebar;
